import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import Seo from "@/components/feature/Seo";
import JsonLd from "@/components/feature/JsonLd";
import { articles } from "@/mocks/articles";
import { mangas } from "@/mocks/mangas";
import { getArticleBySlug, getMangasByCategory } from "@/utils/content";

const BASE_URL = "https://mangistart.github.io";

export default function Article() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-24 text-center md:px-6">
          <h1 className="font-heading text-2xl text-foreground-900">未找到该文章</h1>
          <p className="mt-3 text-foreground-600">抱歉，这篇文章不存在或已被移动。</p>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600"
          >
            返回首页
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const related = mangas.filter((m) => m.category === article.category);
  const relatedArticles = articles.filter((a) => a.slug !== article.slug).slice(0, 3);
  const dateISO = article.date.replace(/-/g, "-");

  return (
    <>
      <Seo
        title={`${article.title} - 漫画分类大全导航`}
        description={article.excerpt}
        keywords={`${article.categoryName},漫画分类大全,漫画推荐大全,漫画排行榜大全`}
      />
      <JsonLd
        data={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: dateISO,
            dateModified: dateISO,
            author: { "@type": "Organization", name: "漫途导航" },
            publisher: { "@type": "Organization", name: "漫途导航", logo: { "@type": "ImageObject", url: `${BASE_URL}/logo.png` } },
            mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE_URL}/article/${article.slug}` },
            articleSection: article.categoryName,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${BASE_URL}/` },
              { "@type": "ListItem", position: 2, name: "漫画分类大全", item: `${BASE_URL}/category` },
              { "@type": "ListItem", position: 3, name: article.categoryName, item: `${BASE_URL}/category/${article.category}` },
              { "@type": "ListItem", position: 4, name: article.title },
            ],
          },
        ]}
      />
      <div className="flex min-h-screen flex-col bg-background-50">
        <Navbar />
        <main className="flex-1">
          <Breadcrumb
            items={[
              { label: "漫画分类大全", path: "/category" },
              { label: article.categoryName, path: `/category/${article.category}` },
              { label: article.title },
            ]}
          />

          <article className="mx-auto w-full max-w-4xl px-4 py-10 md:px-6">
            <header className="text-center">
              <div className="flex items-center justify-center gap-2">
                <Link
                  to={`/category/${article.category}`}
                  className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 hover:bg-primary-200"
                >
                  {article.categoryName}
                </Link>
                <span className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800">漫画推荐指南</span>
              </div>
              <h1 className="mt-4 font-heading text-2xl leading-snug text-foreground-900 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line text-primary-500" />
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line text-primary-500" />
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line text-primary-500" />
                  {article.views} 阅读
                </span>
              </div>
            </header>

            <figure className="mt-8 overflow-hidden rounded-lg border border-background-200/70">
              <img src={article.cover} alt={article.title} className="h-auto w-full object-cover" />
            </figure>

            <div className="mt-8">
              <p className="border-l-4 border-primary-500 bg-background-100 px-5 py-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                {article.excerpt}
              </p>
            </div>

            <div className="mt-6 space-y-5">
              {article.content.map((p, idx) => (
                <p key={idx} className="text-base leading-loose text-foreground-800">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-6">
              {article.tags.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600"
                >
                  # {t}
                </span>
              ))}
            </div>
          </article>

          {/* 同类漫画 */}
          {related.length > 0 && (
            <section className="mx-auto w-full max-w-7xl px-4 pb-10 md:px-6" aria-label="同类推荐漫画">
              <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">
                相关{article.categoryName}漫画推荐
              </h2>
              <div className="mt-5 grid grid-cols-2 gap-5 sm:grid-cols-4">
                {related.slice(0, 4).map((m) => (
                  <Link
                    key={m.slug}
                    to={`/category/${m.category}`}
                    className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="relative h-44 w-full overflow-hidden md:h-48">
                      <img
                        src={m.cover}
                        alt={m.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <h3 className="truncate text-sm font-medium text-foreground-900 group-hover:text-primary-600">
                        {m.title}
                      </h3>
                      <p className="mt-1 text-xs text-foreground-500">{m.rating} 分 · {m.chapters} 话</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}

          {/* 更多文章 */}
          <section className="w-full bg-background-100" aria-label="更多推荐文章">
            <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-6">
              <h2 className="font-heading text-xl text-foreground-900 md:text-2xl">更多漫画推荐大全</h2>
              <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-3">
                {relatedArticles.map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="group overflow-hidden rounded-lg border border-background-200/70 bg-background-50 transition-transform duration-300 hover:-translate-y-1"
                  >
                    <div className="relative h-40 w-full overflow-hidden">
                      <img
                        src={a.cover}
                        alt={a.title}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <span className="absolute left-2 top-2 rounded-full bg-primary-500 px-2.5 py-0.5 text-xs text-background-50">
                        {a.categoryName}
                      </span>
                    </div>
                    <div className="p-5">
                      <h3 className="line-clamp-2 text-sm font-medium leading-relaxed text-foreground-900 group-hover:text-primary-600">
                        {a.title}
                      </h3>
                      <p className="mt-2 text-xs text-foreground-500">{a.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
}